import type { CncSpeciesAssignment, ProposalNodeSet, TimberSupplierInvite } from '../types';

/**
 * Board feet per species across enabled structures: item `boardFeet × quantity`,
 * multiplied by the structure quantity. Keys are trimmed species names.
 */
export function proposalBoardFeetBySpecies(nodeSets: ProposalNodeSet[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const ns of nodeSets) {
    if (ns.enabled === false) continue;
    const structureQty = ns.quantity || 1;
    for (const item of ns.materials?.timberList || []) {
      const species = item.species?.trim();
      if (!species) continue;
      const bdft = (item.boardFeet || 0) * (item.quantity || 0) * structureQty;
      if (bdft <= 0) continue;
      out[species] = (out[species] || 0) + bdft;
    }
  }
  for (const species of Object.keys(out)) {
    out[species] = Math.round(out[species]);
  }
  return out;
}

/**
 * Board feet a CNC shop is asked to quote — sum of its species-assignment board
 * feet when the invite is split by species, else the project-level fallback.
 */
export function cncInviteBoardFeet(
  invite: Pick<TimberSupplierInvite, 'cncSpeciesAssignments'> | null | undefined,
  fallback: number,
): number {
  const assignments = invite?.cncSpeciesAssignments;
  if (!assignments?.length) return fallback;
  const total = assignments.reduce((sum, a) => sum + (a.boardFeet || 0), 0);
  return total > 0 ? Math.round(total) : fallback;
}

/** Compact per-species chip, e.g. `White Oak · 3,400 bdft` (species only when bdft missing). */
export function cncAssignmentChipLabel(assignment: CncSpeciesAssignment): string {
  const species = assignment.species.trim();
  const bdft = assignment.boardFeet || 0;
  return bdft > 0 ? `${species} · ${bdft.toLocaleString()} bdft` : species;
}

/**
 * Human material-flow line for one assigned species.
 * `supplier` audience is second person ("Your shop provides…"); `staff` is neutral.
 */
export function cncMaterialFlowLabel(
  assignment: CncSpeciesAssignment,
  audience: 'supplier' | 'staff' = 'supplier',
): string {
  const species = assignment.species.trim();
  const source = assignment.sourceSupplierName?.trim();
  if (assignment.materialSource === 'lctf_shipped') {
    const sourced = source ? ` (sourced from ${source})` : '';
    return audience === 'supplier'
      ? `LCTF ships the ${species} to you${sourced}`
      : `LCTF ships the ${species}${sourced}`;
  }
  return audience === 'supplier'
    ? `Your shop provides the ${species} stock`
    : `Shop provides the ${species} stock`;
}

type PriorCncMaterialPackage = {
  supplierOrgId?: string;
  createdAt?: Date;
  cncPackagePayload?: { speciesAssignments?: CncSpeciesAssignment[] } | null;
};

/**
 * A later CNC release keeps "LCTF ships this species" from an earlier package
 * for the same shop. Board feet and notes stay on the current assignment.
 * Shop-provides on a newer package does not erase an earlier LCTF-ships choice.
 */
export function inheritCncMaterialFlowFromPackages(
  assignments: readonly CncSpeciesAssignment[] | undefined,
  packages: readonly PriorCncMaterialPackage[],
  supplierOrgId: string,
): CncSpeciesAssignment[] | undefined {
  if (!assignments) return undefined;
  if (!assignments.length) return [];
  const prior = packages
    .filter((pkg) => pkg.supplierOrgId === supplierOrgId)
    .slice()
    .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
  const carried = new Map<string, CncSpeciesAssignment>();
  for (const pkg of prior) {
    for (const row of pkg.cncPackagePayload?.speciesAssignments ?? []) {
      const key = row.species.trim().toLowerCase();
      if (!key || carried.has(key) || row.materialSource !== 'lctf_shipped') continue;
      carried.set(key, row);
    }
  }
  return assignments.map((assignment) => {
    if (assignment.materialSource === 'lctf_shipped') return assignment;
    const priorRow = carried.get(assignment.species.trim().toLowerCase());
    if (!priorRow) return assignment;
    const next: CncSpeciesAssignment = { ...assignment, materialSource: 'lctf_shipped' };
    const orgId = priorRow.sourceSupplierOrgId?.trim();
    const name = priorRow.sourceSupplierName?.trim();
    if (orgId) next.sourceSupplierOrgId = orgId;
    if (name) next.sourceSupplierName = name;
    return next;
  });
}

/**
 * Trim fields, drop assignments without a species, and omit empty optional keys
 * entirely (Firestore rejects `undefined` inside nested array objects). Source
 * supplier fields are only kept when LCTF ships the material.
 */
export function sanitizeCncSpeciesAssignments(
  assignments: CncSpeciesAssignment[] | undefined,
): CncSpeciesAssignment[] {
  const out: CncSpeciesAssignment[] = [];
  for (const a of assignments || []) {
    const species = a.species?.trim();
    if (!species) continue;
    const clean: CncSpeciesAssignment = { species, materialSource: a.materialSource };
    if (typeof a.boardFeet === 'number' && a.boardFeet > 0) {
      clean.boardFeet = Math.round(a.boardFeet);
    }
    if (a.materialSource === 'lctf_shipped') {
      const orgId = a.sourceSupplierOrgId?.trim();
      const name = a.sourceSupplierName?.trim();
      if (orgId) clean.sourceSupplierOrgId = orgId;
      if (name) clean.sourceSupplierName = name;
    }
    const note = a.note?.trim();
    if (note) clean.note = note;
    out.push(clean);
  }
  return out;
}
